import type {
    ChartAccount,
    OperationAmountSource,
    OperationHistoryVersion,
    OperationLine,
    OperationLineSide,
    OperationPairRole,
    OperationVersionTimelineStatus,
} from '@/api/models';
import { lineBoxCopy, type LineBoxCopy } from '../../editor/pairBoxMeta';

/**
 * The catalog's read-only look at a recipe: what this operation posts, without
 * opening the editor.
 *
 * Pure, like every other `*Meta` in this folder — the modal renders what this
 * returns and reaches for nothing else.
 *
 * **The boxes are the editor's boxes.** `lineBoxCopy` is what the editor's
 * closed `LineBox` reads, and it is reused here rather than reimplemented: a
 * preview that described a line differently from the screen one click away
 * would be a second opinion about the same recipe.
 */

/** One leg of a pair, as the modal draws it. */
export type PreviewLineBox = LineBoxCopy & { side: OperationLineSide };

/** One balanced Dr/Cr pair. */
export type PreviewPair = {
    pairGroup: number;
    /** 1-based position on screen. Positional, so a hole in `pairGroup` cannot show. */
    displayNumber: number;
    role: OperationPairRole;
    /**
     * `null` when the version carries only one leg of this pair. The server
     * refuses to publish such a version, so it should not happen — and the modal
     * says which half is missing rather than dropping the pair, because a recipe
     * that silently lost a line is exactly what somebody opening a preview needs
     * to be told.
     */
    debit: PreviewLineBox | null;
    credit: PreviewLineBox | null;
    /** Which figure the pair takes, as the sentence under it. */
    amount: AmountNote;
};

/**
 * Which figure a pair takes, as a phrase.
 *
 * The history screen's vocabulary, reused: "amount without VAT" reads inside a
 * sentence, which is what `Amount: …` is, where the editor's picker label
 * ("Amount without VAT") is a heading for a control.
 */
export const amountSourceKey = (source: OperationAmountSource): string =>
    `operations.history.amountSource.${source}`;

/**
 * The `Amount: …` line under a pair.
 *
 * Two parts, because they answer two questions: `valueKey` says which figure the
 * pair takes, and `fraction` — present only on a VAT pair — says what the tenant's
 * rate works that out to. The fraction is the one that catches a recipe built
 * against the wrong rate, which no other line on this screen can.
 */
export type AmountNote = {
    valueKey: string;
    fraction: { key: string; params: Record<string, number> } | null;
};

/**
 * Read the pair's figure rule, with the rate in force spelled out.
 *
 * The editor's own copy for the fraction (`18/118`) and for a tenant with no
 * rate set: the two screens describe one rule and must not word it twice.
 */
export const amountNote = (
    source: OperationAmountSource,
    vatRateBps: number,
): AmountNote => ({
    valueKey: amountSourceKey(source),
    fraction:
        source !== 'VAT_OF_GROSS'
            ? null
            : vatRateBps > 0
              ? {
                    key: 'operations.editor.vatFraction',
                    params: {
                        rate: vatRateBps / 100,
                        gross: 100 + vatRateBps / 100,
                    },
                }
              : { key: 'operations.editor.vatNoRate', params: {} },
});

/** A published version, resolved for display. */
export type TemplatePreview = {
    versionId: string;
    version: number;
    /** Derived server-side: in force, scheduled, superseded or blocked. */
    status: OperationVersionTimelineStatus;
    /** `01/08/2026`. */
    effectiveFromText: string;
    pairs: PreviewPair[];
};

/**
 * The newest version that is not a draft.
 *
 * **Not `activeVersion`.** What is in force *today* and what was published last
 * are different versions whenever a change is scheduled for next month, and the
 * catalog's preview is a look at the recipe as it now stands — so a scheduled
 * version is the answer, labelled `Scheduled`, rather than hidden behind the one
 * it is about to replace.
 *
 * Compared by **version number**, not by list position: numbers are monotonic
 * but not contiguous — a deleted draft leaves a hole — and the response's order
 * is the server's business.
 */
export const latestPublishedVersion = (
    versions: readonly OperationHistoryVersion[],
): OperationHistoryVersion | null =>
    versions
        .filter((version) => version.status !== 'DRAFT')
        .reduce<OperationHistoryVersion | null>(
            (best, version) =>
                best === null || version.version > best.version
                    ? version
                    : best,
            null,
        );

/** Group a version's lines into pairs, in the order an entry is written. */
export const toPreviewPairs = (
    lines: readonly OperationLine[],
    accounts: readonly ChartAccount[],
    vatRateBps: number,
): PreviewPair[] => {
    const groups = new Map<number, OperationLine[]>();
    for (const line of lines) {
        const existing = groups.get(line.pairGroup);
        if (existing) {
            existing.push(line);
        } else {
            groups.set(line.pairGroup, [line]);
        }
    }

    return [...groups.entries()]
        .sort(([a], [b]) => a - b)
        .map(([pairGroup, pairLines], index) => {
            const debit = pairLines.find((line) => line.side === 'DEBIT');
            const credit = pairLines.find((line) => line.side === 'CREDIT');
            const box = (
                line: OperationLine | undefined,
            ): PreviewLineBox | null =>
                line
                    ? { ...lineBoxCopy(line, accounts), side: line.side }
                    : null;

            return {
                pairGroup,
                displayNumber: index + 1,
                role: (debit ?? credit)?.pairRole ?? 'OTHER',
                debit: box(debit),
                credit: box(credit),
                amount: amountNote(
                    (debit ?? credit)?.amountSource ?? 'GROSS',
                    vatRateBps,
                ),
            };
        });
};
